'use client';

import { useState, useEffect } from 'react';
import { pickEs } from '@/lib/utils/pickEs';

/**
 * Legacy docs may store name/description as { es, en, pt } from the T1-T4 era.
 * Normalize to plain string so React JSX can render directly without #31.
 */
function normalizeActivity(raw: Record<string, unknown>): Activity {
  return {
    ...(raw as unknown as Activity),
    name: pickEs(raw.name),
    description: pickEs(raw.description) || undefined,
    shortDescription: pickEs(raw.shortDescription) || undefined,
    subtitle: pickEs(raw.subtitle) || undefined,
  };
}

export interface Activity {
  id: string;
  name: string;
  slug: string;
  subtitle?: string;
  description?: string;
  shortDescription?: string;
  basePrice: {
    amount: number;
    currency: string;
    perPerson: boolean;
  };
  childPrice?: {
    amount: number;
    currency: string;
    perPerson: boolean;
  };
  category?: string;
  difficulty?: 'easy' | 'moderate' | 'challenging' | 'expert';
  durationHours: number;
  heroImage?: string;
  images?: string[];
  focalPoint?: { x: number; y: number };
  included?: string[];
  notIncluded?: string[];
  requirements?: string[];
  minParticipants?: number;
  maxParticipants?: number;
  availableDays?: number[];
  blockedDates?: string[];
  location?: {
    name?: string;
    latitude?: number;
    longitude?: number;
  };
  highlights?: string[];
  whatToBring?: string[];
  itinerary?: Array<{
    time: string;
    title: string;
    description: string;
  }>;
  dailyPricing?: Record<string, number>;
  childPricePercent?: number;
  extraFields?: Array<{
    key: string;
    label: { en: string; es: string; pt?: string };
    type: 'text' | 'textarea' | 'time' | 'number';
    required: boolean;
    placeholder?: { en: string; es: string; pt?: string };
  }>;
}

// API base URL (for static export compatibility)
const getApiUrl = () => {
  const apiBase = process.env.NEXT_PUBLIC_API_URL;
  return apiBase ? `${apiBase}/api` : '/api';
};

/**
 * Hook to fetch all active activities
 *
 * @returns Object with activities array, loading state, and error
 *
 * @example
 * const { activities, loading, error } = useActivities();
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error} />;
 *
 * return activities.map(activity => <ActivityCard key={activity.id} activity={activity} />);
 */
export function useActivities() {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const apiUrl = getApiUrl();

    fetch(`${apiUrl}/activities`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          setActivities((data.data as Record<string, unknown>[]).map(normalizeActivity));
        } else {
          setError(data.error?.message || 'Failed to fetch activities');
        }
      })
      .catch((err) => {
        console.error('[useActivities] Error:', err);
        setError(err.message || 'Failed to fetch activities');
      })
      .finally(() => setLoading(false));
  }, []);

  return { activities, loading, error };
}

/**
 * Hook to fetch a single activity by ID
 *
 * @param activityId - The activity ID to fetch
 * @returns Object with activity data, loading state, and error
 *
 * @example
 * const { activity, loading, error } = useActivity('heli-skiing');
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error} />;
 * if (!activity) return <NotFound />;
 *
 * return <ActivityDetail activity={activity} />;
 */
export function useActivity(activityId: string | null) {
  const [activity, setActivity] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activityId) {
      setLoading(false);
      return;
    }

    const apiUrl = getApiUrl();

    setLoading(true);
    setError(null);

    fetch(`${apiUrl}/activities/${activityId}`)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error('Activity not found');
          }
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          setActivity(normalizeActivity(data.data as Record<string, unknown>));
        } else {
          setError(data.error?.message || 'Failed to fetch activity');
        }
      })
      .catch((err) => {
        console.error('[useActivity] Error:', err);
        setError(err.message || 'Failed to fetch activity');
      })
      .finally(() => setLoading(false));
  }, [activityId]);

  return { activity, loading, error };
}
